import { useEffect, useState } from "react";
import { Card } from "@comai-portal/design-system";
import { fetchHonestyRatio, type HonestyRatio } from "../services/honestyRatio";

/**
 * The live honesty gauge, shared by /honesty and /trust (CO 613). One component
 * and one fetch path, so the two pages cannot show different numbers.
 */
export function LiveHonestyGauge() {
  const [ratio, setRatio] = useState<HonestyRatio | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchHonestyRatio()
      .then((r) => {
        if (alive) setRatio(r);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : "fetch failed");
      });
    return () => {
      alive = false;
    };
  }, []);

  return <Gauge ratio={ratio} error={error} />;
}

/** The gauge card: error, loading, empty and live states. */
function Gauge({
  ratio,
  error,
}: {
  ratio: HonestyRatio | null;
  error: string | null;
}) {
  if (error) {
    return (
      <Card className="text-center">
        <p className="ds-eyebrow mb-3">Honesty ratio</p>
        <p className="text-mute dark:text-mute-soft">
          The live feed is unreachable right now. Even this we won't fake — the
          number isn't shown because we can't currently read it, not because
          it's inconvenient.
        </p>
      </Card>
    );
  }

  if (!ratio) {
    return (
      <Card className="text-center">
        <p className="ds-eyebrow mb-3">Honesty ratio</p>
        <div className="mx-auto h-16 w-40 animate-pulse rounded-lg bg-ink/5 dark:bg-paper/5" />
        <p className="text-sm text-mute dark:text-mute-soft mt-4">
          Reading the live chain…
        </p>
      </Card>
    );
  }

  if (ratio.percent === null) {
    return (
      <Card className="text-center">
        <p className="ds-eyebrow mb-3">Honesty ratio</p>
        <p className="text-mute dark:text-mute-soft">
          {ratio.reason ??
            "No evidence chain is available right now. We'd rather show you nothing than a made-up number."}
        </p>
      </Card>
    );
  }

  const pct = ratio.percent;

  return (
    <Card accent className="text-center">
      <p className="ds-eyebrow mb-4">Honesty ratio · whole platform</p>
      <div className="flex items-baseline justify-center gap-3">
        <span className="font-display font-semibold leading-none text-[clamp(3.5rem,12vw,6rem)]">
          {pct}%
        </span>
      </div>
      <p className="text-sm text-mute dark:text-mute-soft mt-3">
        {ratio.passed} of {ratio.total} units in the proof chain have passed QC
      </p>

      {/* Single progress bar: passed vs. the rest of the chain */}
      <div className="mt-8">
        <div className="h-3 w-full overflow-hidden rounded-full bg-ink/5 dark:bg-paper/5">
          <div className="h-full bg-signal" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-mute dark:text-mute-soft">
          <span>
            <span className="inline-block h-2 w-2 rounded-full bg-signal align-middle mr-2" />
            {ratio.passed} passed
          </span>
          <span>
            <span className="inline-block h-2 w-2 rounded-full bg-ink/15 dark:bg-paper/15 align-middle mr-2" />
            {ratio.grandfathered} grandfathered
          </span>
        </div>
      </div>

      {/* Provenance — the number is live and reads the same source the
          internal instrument panel reads. */}
      <div className="mt-8 border-t border-ink/10 dark:border-paper/10 pt-4">
        <p className="text-xs text-mute dark:text-mute-soft">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle mr-2" />
          Live — read from the same federation tool the internal instrument
          panel reads, not a number staged for a slide.
        </p>
      </div>
    </Card>
  );
}
